/**
 * FilterChip — active-filter chip (design.md §5 Chips):
 * Lavender fill + Blue-light border + Navy text, radius 6px, count inline
 * ("Alle 8"). Inactive chips render tertiary-style (subtle --secondary fill,
 * muted text, no visible border) so the colored look = the active filter.
 *
 * Dark (§9 active-chip mapping): --selected surface + Periwinkle text.
 * ⚠ INTERPOLATED VALUE — flag for Sebastian's review: design.md §9 gives the
 * dark active-chip surface + text but NO border; periwinkle @ 30%
 * (border-brand-periwinkle/30) is the documented U1 choice.
 *
 * §2 color discipline: brand color marks the active selection only — never
 * decorate a whole filter bar. Status/soft-badge colors never appear here.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  /** Optional inline count, rendered muted after the label (e.g. "Alle 8"). */
  count?: number | string;
  /** Colored (lavender/navy) look = the active filter. Defaults to inactive. */
  active?: boolean;
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ label, count, active = false, className, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-brand-blue-light bg-brand-lavender text-brand-navy dark:border-brand-periwinkle/30 dark:bg-selected dark:text-selected-foreground'
          : 'border-transparent bg-secondary text-muted-foreground hover:text-foreground',
        className,
      )}
      {...props}
    >
      {label}
      {count !== undefined && <span className="opacity-70">{count}</span>}
    </button>
  ),
);
FilterChip.displayName = 'FilterChip';
